import React from "react";
import { PartnerLicenceListType } from "../../../../../../redux/slices/partner-program/licence-management/Types";
import {
  Button,
  Iconography,
  IconographySizes,
  Loader,
} from "@hid-galaxy-ui/galaxy-react";
import { IconButton, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  AddPartnerLicenceDetailsType,
  addPartnerLicenceListSagaAction,
  deletePartnerLicenceListSagaAction,
  getPartnerLicenceListSagaAction,
  updatePartnerLicenceListPayloadType,
  updatePartnerLicenceListSagaAction,
} from "../../../../../../redux/saga/partner-program/licence-management/saga-actions";
import PartnerLicenceModal from "./partnerLicenceModal";
import { formatDate } from "../tier-management";
import { LoaderSizes } from "@hid-galaxy-ui/galaxy-react/components/Loader/loaderEnums";
import DeleteModal from "../../../../../../common/components/DeleteModal";

type Props = {
  activeTab: number;
  partnerProgramId: number;
  readOnly?: boolean;
};

const PartnerLicenceManagement: React.FC<Props> = ({
  activeTab,
  partnerProgramId,
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const {
    partnerLicenceList,
    status: { partnerLicenceListLoading, deletePartnerLicenceLoading },
  } = useAppSelector((state) => state.partner_licenceProgram);

  const [showAdd, setShowAdd] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);
  const [editLicence, setEditLicence] = React.useState<
    PartnerLicenceListType[number] | null
  >(null);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteLicenceId, setDeleteLicenceId] = React.useState<number | null>(
    null
  );

  React.useEffect(() => {
    if (activeTab === 2 && partnerLicenceList?.length === 0) {
      dispatch(getPartnerLicenceListSagaAction(partnerProgramId));
    }
  }, [activeTab, dispatch, partnerProgramId, partnerLicenceList?.length]);

  const toggleRow = (id: number) =>
    setExpandedId((prev) => (prev === String(id) ? null : String(id)));

  const onAddClick = () => setShowAdd(true);

  const onEditClick = (licence: PartnerLicenceListType[number]) => {
    setEditLicence(licence);
    setShowEdit(true);
  };

  const onDeleteLicence = (id: number) => {
    dispatch(
      deletePartnerLicenceListSagaAction({
        ID: id,
        setOpen: setDeleteModalOpen,
      })
    );
    // dispatch(getPartnerLicenceListSagaAction(partnerProgramId));
  };

  const onDeleteClick = (id: number) => {
    setDeleteModalOpen(true);
    setDeleteLicenceId(id);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: "12px",
          marginBottom: "12px",
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "Roboto",
              fontStyle: "normal",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "20px",
              letterSpacing: "0.2px",
              color: "#0A0A0A",
              marginBottom: "5px",
            }}
          >
            License Management
          </h1>
          <Typography
            variant="h4"
            style={{
              fontFamily: "Roboto",
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "18px",
              letterSpacing: "0.2px",
              color: "#4A5565",
            }}
          >
            Add License Configure license types and allocations
          </Typography>
        </div>
        <Button
          variant="primary"
          label="Add License"
          icon={<Iconography icon="plus" />}
          onClick={onAddClick}
          disabled={readOnly}
        />
      </div>
      <div className="pp-table" role="table" aria-label="License management">
        <div className="pp-table-header" role="rowgroup">
          <div
            className="pp-th col-expander"
            role="columnheader"
            aria-hidden="true"
          />
          <div className="pp-th col-licence-type" role="columnheader">
            License Type
          </div>
          <div className="pp-th col-count" role="columnheader">
            Count
          </div>
          <div className="pp-th col-points" role="columnheader">
            Points
          </div>
          <div className="pp-th col-created" role="columnheader">
            Created At
          </div>
          <div className="pp-th col-actions" role="columnheader">
            Actions
          </div>
        </div>
        <div className="pp-table-body" role="rowgroup">
          {partnerLicenceListLoading ? (
            <div
              style={{
                padding: "16px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Loader variant={LoaderSizes.medium} />
            </div>
          ) : (partnerLicenceList ?? []).length === 0 ? (
            <div
              style={{
                padding: "16px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              No licenses found
            </div>
          ) : (
            (partnerLicenceList ?? []).map((licence, index) => {
              const isExpanded = expandedId === String(licence.ID);

              return (
                <div
                  className="pp-row"
                  role="row"
                  key={licence.ID}
                  style={index === 0 ? { borderTop: "none" } : {}}
                >
                  <div className="pp-td col-expander" role="cell">
                    <button
                      type="button"
                      className="pp-expander"
                      onClick={() => toggleRow(licence.ID)}
                      aria-expanded={isExpanded}
                      aria-controls={`licence-desc-${licence.ID}`}
                      title={isExpanded ? "Collapse" : "Expand"}
                    >
                      <Iconography
                        icon={isExpanded ? "chevronUp" : "chevronDown"}
                        size={IconographySizes.Small}
                      />
                    </button>
                  </div>
                  <div className="pp-td col-licence-type" role="cell">
                    {licence.license_type}
                  </div>
                  <div className="pp-td col-count" role="cell">
                    {Number(licence.license_count).toLocaleString()}
                  </div>
                  <div className="pp-td col-points" role="cell">
                    {Number(licence.points).toLocaleString()}
                  </div>
                  <div className="pp-td col-created" role="cell">
                    {formatDate(licence.created_at)}
                  </div>
                  <div className="pp-td col-actions" role="cell">
                    <IconButton
                      aria-label={`Edit ${licence.license_type}`}
                      onClick={() => onEditClick(licence)}
                      disabled={readOnly}
                    >
                      <Iconography icon="pen" size={IconographySizes.Small} />
                    </IconButton>
                    <IconButton
                      aria-label={`Delete ${licence.license_type}`}
                      onClick={() => onDeleteClick(licence.ID)}
                      disabled={readOnly}
                    >
                      <Iconography
                        icon="trashCan"
                        size={IconographySizes.Small}
                      />
                    </IconButton>
                  </div>
                  {isExpanded && (
                    <div
                      className="pp-row-expand"
                      id={`licence-desc-${licence.ID}`}
                      role="region"
                      aria-label={`${licence.license_type} details`}
                    >
                      <div className="pp-row-expand-inner">
                        <div className="pp-row-expand-label">Description</div>
                        <Typography
                          variant="body2"
                          className="pp-row-expand-text"
                        >
                          {licence.comments.trim()?.length
                            ? licence.comments
                            : "—"}
                        </Typography>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      {showAdd && (
        <PartnerLicenceModal
          mode="add"
          open
          onClose={() => setShowAdd(false)}
          onSubmit={(payload: AddPartnerLicenceDetailsType) => {
            const license_count = Number(payload.license_count);
            const points = Number(payload.points);

            const clean: AddPartnerLicenceDetailsType = {
              ID: payload.ID,
              license_type: payload.license_type?.trim() ?? "",
              license_count: Number.isFinite(license_count) ? license_count : 0,
              points: Number.isFinite(points) ? points : 0,
              comments: payload.comments?.trim() ?? "",
            };
            if (!clean.license_type) {
              console.error("License Type is required.");
              return;
            }

            dispatch(addPartnerLicenceListSagaAction(clean));
            // dispatch(getPartnerLicenceListSagaAction(partnerProgramId));
          }}
        />
      )}

      {showEdit && editLicence && (
        <PartnerLicenceModal
          onClose={() => {
            setShowEdit(false);
            setEditLicence(null);
          }}
          onSubmit={(payload: updatePartnerLicenceListPayloadType) => {
            const clean = {
              ...payload,
              ID: editLicence.ID,
              license_type: payload.license_type,
              license_count: Number(payload.license_count),
              points: Number(payload.points),
              comments: payload.comments,
            } as any;
            dispatch(updatePartnerLicenceListSagaAction(clean));
            // dispatch(getPartnerLicenceListSagaAction(partnerProgramId));
          }}
          mode="edit"
          open
          initialValue={{
            ID: editLicence.ID,
            license_type: editLicence.license_type,
            license_count: editLicence.license_count,
            points: Number(editLicence.points) as any,
            comments: editLicence.comments,
          }}
        />
      )}
      {deleteModalOpen && deleteLicenceId && (
        <DeleteModal
          setDeleteOpen={setDeleteModalOpen}
          onDeleteButtonClick={() => onDeleteLicence(deleteLicenceId)}
          deleteButtonLoading={deletePartnerLicenceLoading}
          optionalHeader="Delete License"
          message="Are you sure you want to delete this license?"
        />
      )}
    </div>
  );
};

export default PartnerLicenceManagement;
